import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import { compose } from 'react-apollo';
import { updateAssessmentDataCache } from 'legacy/src/actions/assessmentData';
import {
  withQuizQuestionInfo,
  hasNonSecureQn,
  withAssessmentSecurityInfo,
  withSavePreference,
  withMeQuery,
  withTeacherInfo,
} from 'legacy/src/components/util';
import { savePreferenceSuccess as savePreferenceSuccessAction } from 'legacy/src/components/auth/actions';
import AttestationDialog from 'legacy/src/components/homepage/AttestationDialog';
import { assignAssessment } from '../../state/actions';
import AssignManageForm from 'chameleon-src/components/assign/AssignManageForm';
import hasPracticeExamBehavior from 'legacy-src/utility/hasPracticeExamBehavior';
import { isFRQ } from 'legacy-src/utility/progressHelpers';
import InfoMessage from 'chameleon/ui-stack/ui/basic/info-message';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import Form from './Form';

import './styles.scss';

/**
 * Assign modal component for both PPC and QB assessments.
 * */
const AssignModal = (props) => {
  const [displayAttestationDialog, setDisplayAttestationDialog] =
    useState(false);
  const {
    assessment,
    itemTitle,
    onAssignModalClose,
    loading,
    itemId,
    assessmentDataCache,
    updateAssessmentDataCache,
    teacherSubject,
    isPreApSubject,
    isAdminOrCoordinator,
    assessmentWithQuestionsData,
    disablePpcAutoAssignFlag,
    isPPC,
    isSPP,
    reviewModeFlag,
    scrambleQuestionsPerStudent,
    useNewAssignModalFlag,
    subjectInfo,
    userData,
    studentsBySection,
    individualStudentAssignmentFlag,
    allowLateSubmissionFF,
    showNewAssignModalCasButton,
    allowHardTimer,
    userPreferences,
    userSubjects,
    bluebookUIFF,
  } = props;
  const { questions, learnosityItemReference } =
    assessmentWithQuestionsData || {};
  let {
    quizHasNonSecureQns,
    isPracticeExam,
    showAttestation,
    quizHasAnySecureQnsPE,
  } = props;
  const modalTitleText = `Assign ${itemTitle}`;

  useEffect(() => {
    typeof quizHasNonSecureQns === 'boolean' &&
      updateAssessmentDataCache(
        { [hasNonSecureQn]: quizHasNonSecureQns, questions },
        itemId,
      );
  }, [quizHasNonSecureQns]);

  const ModalTitle = () => <span className="ellipsis">{modalTitleText}</span>;

  // use value cached in redux if available, otherwise the gql request will return the value from the BE
  const cache = (assessmentDataCache && assessmentDataCache[itemId]) || {};
  const hasAudioRecordOrFileUpload = !!(
    cache[hasNonSecureQn] || quizHasNonSecureQns
  );
  const withPracticeExamBehavior = hasPracticeExamBehavior(
    isPracticeExam,
    questions,
    isPPC,
  );

  const { chosenEducationPeriodCode: educationalPeriod } = userData;

  const renderAssignForm = () => {
    const hasFrqQuestions = questions && !!questions.find(isFRQ);
    const showStudentScoring = !isPreApSubject && hasFrqQuestions;

    return (
      <Fragment>
        {!isAdminOrCoordinator &&
          isPPC &&
          !isPreApSubject &&
          !disablePpcAutoAssignFlag && (
            <InfoMessage>
              Students see Personal Progress Checks as locked until you decide
              they should have access. Select a class to unlock or schedule this
              assessment using the options below.
            </InfoMessage>
          )}
        <AssignManageForm
          {...props}
          isAssign
          isAssessmentForm
          hideSecureToggle={hasAudioRecordOrFileUpload}
          toggleAttestationDialog={() => setDisplayAttestationDialog(true)}
          showAttestationFlag={showAttestation}
          hasSecureQns={quizHasAnySecureQnsPE}
          teacherSubjects={(teacherSubject && teacherSubject.subjects) || []}
          hasAudioRecordOrFileUpload={hasAudioRecordOrFileUpload}
          onClose={onAssignModalClose}
          onSubmit={assignAssessment}
          hasPracticeExamBehavior={withPracticeExamBehavior}
          showStudentScoring={showStudentScoring}
          learnosityReference={learnosityItemReference}
          spacingAroundContent="p-6"
        />
      </Fragment>
    );
  };

  return (
    <Fragment>
      {showAttestation && displayAttestationDialog && (
        <AttestationDialog
          onRequestClose={() => setDisplayAttestationDialog(false)}
          onSuccess={() => setDisplayAttestationDialog(false)}
          onSecondaryAction={() => setDisplayAttestationDialog(false)}
          btnPrimary="OK"
          btnSecondary=""
          transparentBackground
        />
      )}

      <ConfirmDialog
        size="big"
        contentWithSpacing={false}
        title={<ModalTitle />}
        onClose={onAssignModalClose}
        headerWithBorder={!useNewAssignModalFlag}
      >
        {!loading ? (
          <Fragment>
            {useNewAssignModalFlag ? (
              <Form
                assessment={assessment}
                isAdminOrCoordinator={isAdminOrCoordinator}
                params={props?.params}
                router={props?.router}
                isAssign
                isAssessmentForm
                assessmentWithQuestionsData={assessmentWithQuestionsData}
                isSPP={isSPP}
                isPPC={isPPC}
                isPreApSubject={isPreApSubject}
                isPracticeExam={isPracticeExam}
                onAssignModalClose={onAssignModalClose}
                reviewModeFlag={reviewModeFlag}
                scrambleQuestionsPerStudent={scrambleQuestionsPerStudent}
                subjectInfo={subjectInfo}
                assessmentId={itemId}
                educationalPeriod={educationalPeriod}
                studentsBySection={studentsBySection}
                hasAudioRecordOrFileUpload={hasAudioRecordOrFileUpload}
                individualStudentAssignmentFlag={
                  individualStudentAssignmentFlag
                }
                onAssign={props?.onAssign}
                allowLateSubmissionFF={allowLateSubmissionFF}
                showNewAssignModalCasButton={showNewAssignModalCasButton}
                allowHardTimer={allowHardTimer}
                userPreferences={userPreferences}
                userSubjects={userSubjects}
                bluebookUIFF={bluebookUIFF}
              />
            ) : (
              renderAssignForm()
            )}
          </Fragment>
        ) : (
          <LoadingSpinner />
        )}
      </ConfirmDialog>
    </Fragment>
  );
};

AssignModal.displayName = 'AssignModal';

AssignModal.propTypes = {
  // The title of the assessment.
  itemTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  // Handler for letting the parent know to unmount the modal.
  onAssignModalClose: PropTypes.func.isRequired,
  isFinalExam: PropTypes.bool,
  isPilot: PropTypes.bool,
  /*
   * we expect this to also be undefined sometimes, which is okay. we use this value to determine
   * whether or not to run the gql query that fetches this value. unfortunately this
   * will throw a prop validation error which is safe to ignore.
   */
  quizHasNonSecureQns: PropTypes.bool,
  loading: PropTypes.bool,
  acceptedPracticeExamToc: PropTypes.bool,
  me: PropTypes.object.isRequired,
  subjectInfo: PropTypes.object.isRequired,
  assessmentWithQuestionsData: PropTypes.objectOf(PropTypes.any),
};

AssignModal.defaultProps = {
  isFinalExam: false,
  isPilot: false,
  loading: false,
  acceptedPracticeExamToc: false,
  assessmentWithQuestionsData: {},
};

export default compose(
  connect(
    (state) => ({
      assessmentDataCache: state.assessmentDataCache,
      showAttestation: state.featureFlag.show_attestation_confirmation_dialog,
      userData: state.userData,
      isPreApSubject: state.subject.data.isPreApSubject,
      disablePpcAutoAssignFlag: state.featureFlag.disable_ppc_auto_assign,
      showNewAssignModalCasButton:
        state.featureFlag.new_assign_modal_cas_button,
      scrambleQuestionsPerStudent:
        state.featureFlag.scramble_questions_per_student,
      reviewModeFlag: state.featureFlag.review_mode,
      useNewAssignModalFlag: state.featureFlag.new_assign_modal,
      individualStudentAssignmentFlag:
        state.featureFlag.individual_student_assignment,
      allowLateSubmissionFF: state.featureFlag.allow_late_submission,
      subjectInfo: state.subject.data,
      studentsBySection: state.studentsBySection,
      allowHardTimer: state.featureFlag.updated_timer_controls,
      userPreferences: state.userData.me.preferences,
      userSubjects: state.userSubjects,
      bluebookUIFF: state.featureFlag.bluebook_ui,
    }),
    {
      updateAssessmentDataCache,
      savePreferenceSuccess: savePreferenceSuccessAction,
    },
  ),
  withQuizQuestionInfo,
  withAssessmentSecurityInfo,
  withSavePreference,
  withMeQuery,
  withTeacherInfo,
)(AssignModal);
